import React from 'react';
import { CheckCircle2, Circle, Lock, MinusCircle } from 'lucide-react';

/**
 * One challenge day. `compact` renders the small variant used by the
 * collapsed "this week" strip in CalendarPanel.
 */
const DayCard = ({ day, isCompleted, isPartial, onToggle, compact = false }) => {
  const { dayNumber, displayDate, dayName, isToday, isPast, isFuture } = day;

  // Only today is interactive (past days remain locked to history).
  const isClickable = isToday;

  // Determine the styling based on state
  const getCardStyle = () => {
    if (isFuture) {
      // Future days - locked, gray
      return 'bg-gray-700 text-gray-500 cursor-not-allowed border-2 border-gray-600 opacity-50';
    }

    if (isToday) {
      // Today - bright and active
      if (isCompleted) {
        return 'bg-gradient-to-br from-green-400 to-green-500 text-black shadow-lg shadow-green-500/50 border-2 border-green-300';
      }
      if (isPartial) {
        return 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-black shadow-lg shadow-yellow-500/50 border-2 border-yellow-300';
      }
      return 'bg-gray-700 text-cyan-300 border-2 border-cyan-500 shadow-lg shadow-cyan-500/30 animate-pulse';
    }

    if (isPast) {
      // Past days
      if (isCompleted) {
        // Completed past day - pale green
        return 'bg-green-200 text-green-900 border-2 border-green-300 cursor-not-allowed';
      }
      if (isPartial) {
        // Partially completed past day - pale yellow
        return 'bg-yellow-200 text-yellow-900 border-2 border-yellow-300 cursor-not-allowed';
      }
      // Incomplete past day - pale red
      return 'bg-red-200 text-red-900 border-2 border-red-300 cursor-not-allowed';
    }

    return 'bg-gray-700 text-cyan-300 border-2 border-cyan-500/30';
  };

  const handleClick = () => {
    if (isClickable) {
      onToggle();
    }
  };

  // Tooltip explains the cycling interaction for today.
  const title = isToday
    ? 'Click to cycle: not done → complete → partial → not done'
    : undefined;

  const sizing = compact
    ? 'rounded-xl p-1 gap-0.5 min-h-[62px]'
    : 'rounded-xl sm:rounded-3xl p-1.5 sm:p-4 gap-0.5 sm:gap-2 min-h-[78px] sm:min-h-[140px]';

  return (
    <button
      onClick={handleClick}
      disabled={!isClickable}
      title={title}
      aria-label={`Day ${dayNumber}, ${displayDate}${isToday ? ' (today)' : ''}`}
      className={`
        relative font-semibold transition-all duration-300
        flex flex-col items-center justify-center
        ${sizing}
        ${getCardStyle()}
        ${isToday ? 'transform hover:scale-105 active:scale-95' : ''}
      `}
    >
      {/* Day Number */}
      <div className={compact ? 'text-base font-bold leading-none' : 'text-lg sm:text-3xl font-bold leading-none'}>
        {dayNumber}
      </div>

      {/* Date Display */}
      <div className={compact ? 'text-[9px] opacity-80 font-medium' : 'text-[10px] sm:text-xs opacity-80 font-medium'}>
        {displayDate}
      </div>

      {/* Day Name (full size only) */}
      {!compact && (
        <div className="hidden sm:block text-xs opacity-70">
          {dayName}
        </div>
      )}

      {/* Status Icon */}
      <div className={compact ? '' : 'mt-0.5 sm:mt-1'}>
        {isFuture ? (
          <Lock className={compact ? 'w-3 h-3 opacity-50' : 'w-3.5 h-3.5 sm:w-5 sm:h-5 opacity-50'} />
        ) : isCompleted ? (
          <CheckCircle2 className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4 sm:w-6 sm:h-6'} />
        ) : isPartial ? (
          <MinusCircle className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4 sm:w-6 sm:h-6'} />
        ) : (
          <Circle className={compact ? 'w-3.5 h-3.5 opacity-50' : 'w-4 h-4 sm:w-6 sm:h-6 opacity-50'} />
        )}
      </div>

      {/* Today Badge */}
      {isToday && (
        <div
          className={
            compact
              ? 'absolute -top-1.5 -right-1.5 bg-cyan-400 text-black text-[8px] font-bold px-1 py-0.5 rounded-full shadow'
              : 'absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-cyan-400 text-black text-[9px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full shadow-lg'
          }
        >
          TODAY
        </div>
      )}
    </button>
  );
};

export default DayCard;
